import { IonIcon, IonPopover } from '@ionic/react';
import { checkmarkOutline } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useAgentTrackMentionSearch } from '../../hooks/useAgentTrackMentionSearch';
import { AgentContextSource } from '../../hooks/useAgentContextSources';
import { useIcons } from '../../hooks/useIcons';
import { AgentSourceId } from '../../types/AgentSource';
import { AgentAttachedTrack } from './agentAttachedTrack';
import { AgentTrackSearchResults } from './AgentTrackSearchResults';

const stopPopoverDismiss = (event: React.SyntheticEvent) => {
  event.stopPropagation();
  event.nativeEvent?.stopImmediatePropagation?.();
};

interface AgentContextPickerProps {
  triggerId: string;
  sources: AgentContextSource[];
  onInsertTrack: (track: AgentAttachedTrack) => void;
  onAttach: (id: AgentSourceId) => void;
  onDetach: (id: AgentSourceId) => void;
  onConnect: (id: AgentSourceId) => void;
}

/**
 * Picker behind the @ button in the composer. Search resolves a catalog
 * track; the rest of the list attaches optional DJ and streaming sources.
 */
export const AgentContextPicker: React.FC<AgentContextPickerProps> = ({
  triggerId,
  sources,
  onInsertTrack,
  onAttach,
  onDetach,
  onConnect,
}) => {
  const popoverRef = useRef<HTMLIonPopoverElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { icons } = useIcons();
  const [isOpen, setIsOpen] = useState(false);
  const [event, setEvent] = useState<Event | undefined>(undefined);
  const [query, setQuery] = useState('');
  const { results, isSearching, activeIndex, setActiveIndex } =
    useAgentTrackMentionSearch(isOpen && query.length > 0 ? query : null);

  useEffect(() => {
    const trigger = document.getElementById(triggerId);
    if (!trigger) return undefined;

    const open = (clickEvent: Event) => {
      clickEvent.preventDefault();
      setEvent(clickEvent);
      setIsOpen(true);
    };
    trigger.addEventListener('click', open);
    return () => trigger.removeEventListener('click', open);
  }, [triggerId]);

  const dismiss = () => {
    setIsOpen(false);
    setEvent(undefined);
    setQuery('');
    setActiveIndex(0);
    document.getElementById(triggerId)?.blur();
  };

  const selectTrack = (track: AgentAttachedTrack) => {
    dismiss();
    // Insert after the popover has unmounted so no backdrop can eat the UI.
    window.setTimeout(() => onInsertTrack(track), 0);
  };

  const chooseSource = (source: AgentContextSource) => {
    if (!source.connected) {
      dismiss();
      onConnect(source.id);
      return;
    }
    if (source.attached) onDetach(source.id);
    else onAttach(source.id);
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!results.length) return;
      setActiveIndex((index) => (index + 1) % results.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!results.length) return;
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const track = results[activeIndex] ?? results[0];
      if (track) selectTrack(track);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      dismiss();
    }
  };

  return (
    <IonPopover
      ref={popoverRef}
      isOpen={isOpen}
      event={event}
      onDidPresent={() => {
        window.setTimeout(() => searchRef.current?.focus(), 50);
      }}
      onDidDismiss={() => {
        setIsOpen(false);
        setEvent(undefined);
        setQuery('');
        setActiveIndex(0);
      }}
      dismissOnSelect={false}
      side="top"
      alignment="start"
      className="agent-context-popover has-track-search"
    >
      <div
        className="agent-context-popover-body"
        onMouseDown={stopPopoverDismiss}
        onPointerDown={stopPopoverDismiss}
        onClick={stopPopoverDismiss}
      >
        <div className="agent-context-track-search">
          <input
            ref={searchRef}
            type="text"
            className="agent-context-track-input"
            value={query}
            placeholder="Search for a track"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <AgentTrackSearchResults
            className="is-popover"
            results={results}
            activeIndex={activeIndex}
            isSearching={isSearching}
            query={query}
            onActiveIndexChange={setActiveIndex}
            onSelect={selectTrack}
          />
        </div>

        <div className="agent-context-popover-divider" />

        <p className="agent-context-popover-hint">
          Also search with your Crate Hackers library
        </p>

        <div className="agent-context-popover-list">
          {sources.map((source) => (
            <button
              key={source.id}
              type="button"
              className={`agent-context-option${
                source.attached && source.connected ? ' is-attached' : ''
              }`}
              onClick={() => chooseSource(source)}
            >
              <img
                src={icons[source.iconKey] || ''}
                alt=""
                className="agent-context-option-icon"
                style={{
                  visibility: icons[source.iconKey] ? 'visible' : 'hidden',
                }}
              />
              <span className="agent-context-option-name">{source.name}</span>
              {!source.connected && (
                <span className="agent-context-option-state">Connect</span>
              )}
              {source.connected && source.attached && (
                <IonIcon icon={checkmarkOutline} />
              )}
            </button>
          ))}
        </div>
      </div>
    </IonPopover>
  );
};

export default AgentContextPicker;
